import { useEffect, useRef, useState } from 'react';
import type { LayerSwitchDirection } from '../context/LayerSwitchContext';
import { LAYER_SLIDE_MS } from '../constants/layerSlideAnimation';

type SlidePhase = 'idle' | 'exit-up' | 'exit-down' | 'enter-from-below' | 'enter-from-above';

/**
 * レイヤー切替スライド中は exit 完了まで旧値を保持（LayerSlideNumber と同型）。
 * 符号クラス（negative/positive）の色切替ずれ防止用。
 */
export function useLayerSlideDeferred<T>(
  value: T,
  direction: LayerSwitchDirection,
  switchNonce: number,
  slideEnabled: boolean,
): T {
  const [display, setDisplay] = useState(value);
  const committedNonce = useRef(switchNonce);
  const pendingRef = useRef(value);
  pendingRef.current = value;
  const [animPhase, setAnimPhase] = useState<SlidePhase>('idle');
  const isAnimating = animPhase !== 'idle';
  const slidePending = direction !== 0 && switchNonce === committedNonce.current;
  const slideActive = switchNonce !== committedNonce.current || isAnimating;

  useEffect(() => {
    const pending = direction !== 0 && switchNonce === committedNonce.current;
    if (!slideEnabled && !pending && switchNonce === committedNonce.current) {
      committedNonce.current = switchNonce;
      return;
    }
    if (switchNonce === committedNonce.current) return;

    if (!slideEnabled) {
      setDisplay(pendingRef.current);
      setAnimPhase('idle');
      committedNonce.current = switchNonce;
      return;
    }

    if (direction === 0) {
      setDisplay(pendingRef.current);
      setAnimPhase('idle');
      committedNonce.current = switchNonce;
      return;
    }

    const exitPhase: SlidePhase = direction === 1 ? 'exit-up' : 'exit-down';
    const enterPhase: SlidePhase = direction === 1 ? 'enter-from-below' : 'enter-from-above';
    const targetNonce = switchNonce;

    setAnimPhase(exitPhase);

    const tEnter = window.setTimeout(() => {
      setDisplay(pendingRef.current);
      setAnimPhase(enterPhase);
    }, LAYER_SLIDE_MS);

    const tIdle = window.setTimeout(() => {
      setAnimPhase('idle');
      committedNonce.current = targetNonce;
    }, LAYER_SLIDE_MS * 2);

    return () => {
      window.clearTimeout(tEnter);
      window.clearTimeout(tIdle);
    };
  }, [switchNonce, direction, slideEnabled]);

  useEffect(() => {
    if (slidePending || slideActive) return;
    setDisplay(value);
  }, [value, switchNonce, slidePending, slideActive]);

  return display;
}
